import React, { useState } from 'react';
import InputMask from 'react-input-mask';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { ApiError, Empresa } from '../../services/tipos';
import { AxiosError } from 'axios';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { Creators as AuthActions } from '../../store/reducer';
import { obterEhAdmin, obterEmailUsuario, obterIdTenent } from '../../services/seguranca';
import { FaImage, FaTrash, FaEye, FaUpload, FaTimes, FaDownload } from 'react-icons/fa';
import { handleDownloadFile } from '../../services/utilitarios';
import { styled } from 'styled-components';

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  border-radius: 6px;
`;

const Spinner = styled.div`
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 3px solid #1F467E;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const FileUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem;
  border: 2px dashed #ced4da;
  border-radius: 8px;
  background-color: #f8f9fa;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 0.5rem;
  text-align: center;
  max-width: 672px; /* Largura equivalente aos campos de formulário */

  &:hover {
    border-color: #80bdff;
    background-color: #e9f5ff;
  }

  &.active {
    border-color: #007bff;
    background-color: #e9f5ff;
  }
`;

const UploadIcon = styled.div`
  font-size: 1.5rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
`;

const UploadText = styled.p`
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
`;

const FileInput = styled.input`
  display: none;
`;

const FilePreviewContainer = styled.div`
  position: relative;
  display: inline-block;
  margin-top: 0.5rem;
  width: 100%;
  max-width: 672px; /* Largura equivalente aos campos de formulário */
`;

const FilePreview = styled.div<{ empty?: boolean }>`
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-radius: 6px;
  margin-top: 0.5rem;
  width: 100%;
  border: 1px solid #dee2e6;
  min-height: 60px;
  visibility: ${props => props.empty ? 'hidden' : 'visible'};
`;

const FileIcon = styled.div`
  margin-right: 0.75rem;
  font-size: 1.5rem;
  color: #6c757d;
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const FileName = styled.p`
  margin: 0;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSize = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: #6c757d;
`;

const FileActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const FileActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  padding: 0 0.5rem;
  transition: all 0.2s;
  color: #6c757d;

  &:hover {
    transform: scale(1.1);
    color: #1F467E;
  }
`;

const ImagePreviewOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 20px;
`;

const ImagePreviewContent = styled.div`
  position: relative;
  max-width: 90%;
  max-height: 90%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 80vh;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const CloseButton = styled.button`
  position: absolute;
  top: -40px;
  right: -40px;
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  z-index: 2001;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.2);
  }
`;

const DownloadButton = styled.button`
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 2001;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }
`;

interface InformacoesTabProps {
    initialData: {
        name: string;
        document: string;
        address: {
            street: string;
            city: string;
            state: string;
            zipCode: string;
            complement: string;
            number: string;
            bairro: string;
        };
        email: string;
        logoUrl?: string;
    };
}

const InformacoesTab: React.FC<InformacoesTabProps> = ({ initialData }) => {
    const dispatch = useAppDispatch();
    const [name, setName] = useState(initialData.name);
    const [dragActive, setDragActive] = useState(false);
    const [city, setCity] = useState(initialData.address.city);
    const [showImageModal, setShowImageModal] = useState(false);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [state, setState] = useState(initialData.address.state);
    const [token] = useState(useAppSelector(state => state.token));
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [street, setStreet] = useState(initialData.address.street);
    const [number, setNumber] = useState(initialData.address.number);
    const [zipCode, setZipCode] = useState(initialData.address.zipCode);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [bairro, setBairro] = useState(initialData.address.bairro || '');
    const [logoPreview, setLogoPreview] = useState(initialData.logoUrl || '');
    const [complement, setComplement] = useState(initialData.address.complement);

    const usuarioAdm = obterEhAdmin(token);

    const EXTENSOES_IMAGEM_PERMITIDAS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    const TAMANHO_MAXIMO_IMAGEM = 10 * 1024 * 1024;

    const fetchAddressByZipCode = async (cep: string) => {
        if (!usuarioAdm) return;

        try {
            const cleanedZipCode = cep.replace(/\D/g, '');
            const response = await fetch(`https://viacep.com.br/ws/${cleanedZipCode}/json/`);
            const data = await response.json();

            if (!data.erro) {
                setStreet(data.logradouro || '');
                setCity(data.localidade || '');
                setState(data.uf || '');
                setComplement(data.complemento || '');
                setBairro(data.bairro || '');
            }
        } catch (error) {
            console.error('Error fetching address:', error);
        }
    };

    const handleZipCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!usuarioAdm) return;

        const value = e.target.value;
        setZipCode(value);

        if (value.match(/^\d{5}-\d{3}$/)) {
            fetchAddressByZipCode(value);
        }
    };

    const validateFields = () => {
        if (!usuarioAdm) return false;

        const newErrors: { [key: string]: string } = {};
        if (!name.trim()) newErrors.name = 'Nome/Razão Social é obrigatório';
        if (!zipCode.trim() || !zipCode.match(/^\d{5}-\d{3}$/))
            newErrors.zipCode = 'CEP é obrigatório e deve estar no formato 00000-000';
        if (!street.trim()) newErrors.street = 'Rua é obrigatória';
        if (!city.trim()) newErrors.city = 'Cidade é obrigatória';
        if (!state.trim()) newErrors.state = 'Estado é obrigatório';
        if (!number.trim()) newErrors.number = 'Número é obrigatório';
        if (!bairro.trim()) newErrors.bairro = 'Bairro é obrigatório';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validarArquivo = (arquivo: File, extensoesPermitidas: string[], tamanhoMaximo: number): { valido: boolean, mensagem?: string } => {
        const extensao = arquivo.name.split('.').pop()?.toLowerCase();
        if (!extensao || !extensoesPermitidas.includes(extensao)) {
            return {
                valido: false,
                mensagem: `Extensão não permitida. Use: ${extensoesPermitidas.join(', ')}`
            };
        }

        if (arquivo.size > tamanhoMaximo) {
            const tamanhoEmMB = (tamanhoMaximo / (1024 * 1024)).toFixed(0);
            return {
                valido: false,
                mensagem: `Arquivo muito grande. Tamanho máximo: ${tamanhoEmMB}MB`
            };
        }

        return { valido: true };
    };

    const handleLogoChange = (file: File) => {
        if (!file) return;

        const validacao = validarArquivo(file, EXTENSOES_IMAGEM_PERMITIDAS, TAMANHO_MAXIMO_IMAGEM);
        if (!validacao.valido) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                logo: validacao.mensagem || 'Imagem inválida'
            }));
            return;
        }

        if (logoPreview && logoPreview.startsWith('blob:')) {
            URL.revokeObjectURL(logoPreview);
        }

        const imageUrl = URL.createObjectURL(file);
        setLogoPreview(imageUrl);
        setLogoFile(file);
        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors['logo'];
            return newErrors;
        });

        handleUploadLogo(file);
    };

    const handleLogoRemove = () => {
        if (logoPreview && logoPreview.startsWith('blob:')) {
            URL.revokeObjectURL(logoPreview);
        }

        setLogoPreview('');
        handleLogoRemovePersist();
        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors['logo'];
            return newErrors;
        });
    };

    const handleDrag = (e: React.DragEvent, active: boolean) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(active);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            if (file.type.startsWith('image/')) {
                handleLogoChange(file);
            } else {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    logo: 'O arquivo não é uma imagem válida'
                }));
            }
        }
    };

    const handleViewImage = () => {
        if (logoPreview) {
            setShowImageModal(true);
        }
    };
    const handleLogoRemovePersist = async () => {
        if (logoPreview && logoPreview.startsWith('blob:')) {
            URL.revokeObjectURL(logoPreview);
        }

        if (initialData.logoUrl) {
            try {
                setIsUploading(true);
                await api.put(`/empresa/deleteLogo/${obterIdTenent(token)}`);
                setLogoPreview('');
                setLogoFile(null);
                toast.success('Logo removida com sucesso!');
            } catch (error) {
                const axiosError = error as AxiosError<ApiError>;
                const errorMessage = axiosError.response?.data?.Mensagem || 'Erro ao remover a logo';
                toast.error(errorMessage);
            } finally {
                setIsUploading(false);
            }
        } else {
            // Se era apenas um preview de uma imagem não enviada ainda
            setLogoPreview('');
            setLogoFile(null);
        }
    };

    const handleUploadLogo = async (file: File) => {
        try {
            setIsUploading(true);
            const formData = new FormData();
            formData.append('logo', file);
            formData.append('idEmpresa', obterIdTenent(token));

            await api.put(`/empresa/uploadLogo`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            toast.success('Logo atualizada com sucesso!');
        } catch (error) {
            const axiosError = error as AxiosError<ApiError>;
            const errorMessage = axiosError.response?.data?.Mensagem || 'Erro ao atualizar a logo';
            toast.error(errorMessage);

            // Reverte o preview em caso de erro
            if (logoPreview && logoPreview.startsWith('blob:')) {
                URL.revokeObjectURL(logoPreview);
            }
            setLogoPreview(initialData.logoUrl || '');
            setLogoFile(null);
        } finally {
            setIsUploading(false);
        }
    };

    const handleUpdateInfo = async () => {
        if (!usuarioAdm) return;
        if (!validateFields()) return;

        try {
            const response = await api.get<Empresa>(`Empresa/${obterIdTenent(token)}`);
            const currentEmpresa = response.data;

            const updatedEmpresa: Empresa = {
                ...currentEmpresa,
                id: obterIdTenent(token),
                nome: name !== initialData.name ? name : currentEmpresa.nome,
                rua: street !== initialData.address.street ? street : currentEmpresa.rua,
                cidade: city !== initialData.address.city ? city : currentEmpresa.cidade,
                estado: state !== initialData.address.state ? state : currentEmpresa.estado,
                cep: zipCode !== initialData.address.zipCode ? zipCode : currentEmpresa.cep,
                complemento: complement !== initialData.address.complement ? complement : currentEmpresa.complemento,
                numero: number !== initialData.address.number ? number : currentEmpresa.numero,
                bairro: bairro !== initialData.address.bairro ? bairro : currentEmpresa.bairro,
            };

            const hasChanges =
                updatedEmpresa.nome !== currentEmpresa.nome ||
                updatedEmpresa.rua !== currentEmpresa.rua ||
                updatedEmpresa.cidade !== currentEmpresa.cidade ||
                updatedEmpresa.estado !== currentEmpresa.estado ||
                updatedEmpresa.cep !== currentEmpresa.cep ||
                updatedEmpresa.complemento !== currentEmpresa.complemento ||
                updatedEmpresa.numero !== currentEmpresa.numero ||
                updatedEmpresa.bairro !== currentEmpresa.bairro;

            if (hasChanges) {
                await api.put('/Empresa', updatedEmpresa);
                toast.success('Informações atualizadas com sucesso!');
                dispatch(AuthActions.setCadastroIncompleto(false));

                const urlParams = new URLSearchParams(window.location.search);
                const isCompleteCadastro = urlParams.has('complete-cadastro');

                if (isCompleteCadastro) {
                    window.location.href = '/planos';
                } else {
                    window.location.href = '/minha-conta';
                }
            } else {
                toast.info('Nenhuma alteração detectada.');
            }

            setErrors({});
        } catch (error) {
            const axiosError = error as AxiosError<ApiError>;
            const errorMessage = axiosError.response?.data?.Mensagem || 'Erro desconhecido';
            toast.error('Não foi possível atualizar as informações: ' + errorMessage);
        }
    };

    const handleDocumentoChangeRequest = () => {
        const message = `Solicito alteração do Documento: ${initialData.document}\nMeu e-mail: ${obterEmailUsuario(token)}`;
        const whatsappUrl = `https://wa.me/5585991491356?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
    };

    const handleNomeChangeRequest = () => {
        const message = `Solicito alteração do Nome/Razão social: ${initialData.name}\nMeu e-mail: ${obterEmailUsuario(token)}`;
        const whatsappUrl = `https://wa.me/5585991491356?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
    };

    return (
        <div className="space-y-1 bg-white dark:bg-gray-900 p-1 rounded-lg">
            <h2 className="text-xl font-semibold text-black dark:text-gray-100 mb-1">Informações Pessoais</h2>

            {!usuarioAdm && (
                <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4">
                    <p className="font-bold">Atenção</p>
                    <p>Apenas usuários administradores podem editar informações da Empresa.</p>
                </div>
            )}

            {/* Seção para upload da logo */}
            {usuarioAdm && (
                <div className="pt-1">
                    <h3 className="text-lg font-medium text-black dark:text-gray-100 mb-1">Sua logo</h3>

                    <FileInput
                        id="logo-upload"
                        type="file"
                        accept="image/*"
                        onChange={(e: any) => {
                            const fileInput = e.target as HTMLInputElement;
                            const file = fileInput.files?.[0];
                            if (file) {
                                handleLogoChange(file);
                                fileInput.value = '';
                            }
                        }}
                    />
                    <FileUploadContainer
                        className={dragActive ? 'active' : ''}
                        onClick={() => !isUploading && document.getElementById('logo-upload')?.click()}
                        onDragEnter={(e: any) => !isUploading && handleDrag(e, true)}
                        onDragLeave={(e: any) => !isUploading && handleDrag(e, false)}
                        onDragOver={(e: any) => !isUploading && e.preventDefault()}
                        onDrop={(e: any) => !isUploading && handleDrop(e)}
                        style={isUploading ? { opacity: 0.6, pointerEvents: 'none' } : {}}
                    >
                        {isUploading ? (
                            <UploadIcon>
                                <Spinner />
                            </UploadIcon>
                        ) : (
                            <>
                                <UploadIcon>
                                    <FaUpload />
                                </UploadIcon>
                                <UploadText>
                                    {dragActive
                                        ? 'Solte a logo aqui'
                                        : 'Arraste e solte uma imagem ou clique para selecionar'}
                                </UploadText>
                            </>
                        )}
                    </FileUploadContainer>

                    <FilePreviewContainer>
                        {logoPreview && (
                            <FilePreview empty={false}>
                                {isUploading && (
                                    <LoadingOverlay>
                                        <Spinner />
                                    </LoadingOverlay>
                                )}
                                <FileIcon>
                                    <FaImage />
                                </FileIcon>
                                <FileInfo>
                                    <FileName>Logo da empresa</FileName>
                                    <FileSize>Clique no ícone para ver</FileSize>
                                </FileInfo>
                                <FileActions>
                                    <FileActionButton
                                        onClick={(e: any) => {
                                            e.preventDefault();
                                            !isUploading && handleViewImage();
                                        }}
                                        title="Visualizar logo"
                                        disabled={isUploading}
                                    >
                                        <FaEye />
                                    </FileActionButton>
                                    <FileActionButton
                                        onClick={(e: any) => {
                                            e.preventDefault();
                                            !isUploading && handleLogoRemove();
                                        }}
                                        title="Remover logo"
                                        disabled={isUploading}
                                    >
                                        <FaTrash />
                                    </FileActionButton>
                                </FileActions>
                            </FilePreview>
                        )}
                    </FilePreviewContainer>
                    {errors.logo && <p className="text-red-500 text-sm mt-1">{errors.logo}</p>}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                <div className="space-y-1">
                    <label className="block text-sm font-medium text-black dark:text-gray-200">
                        Nome/Razão Social
                    </label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => usuarioAdm && setName(e.target.value)}
                        disabled={true}
                        className={`w-full max-w-md p-2 border border-gray rounded-md bg-gray-100 dark:bg-gray text-gray-500 dark:text-gray-400 cursor-not-allowed`}
                    />
                    <button
                        onClick={handleNomeChangeRequest}
                        className="text-sm text-[#1F467E] hover:underline mt-1 focus:outline-none"
                    >
                        Solicitar alteração Nome/Razão social
                    </button>
                    {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
                </div>

                <div className="space-y-1">
                    <label className="block text-sm font-medium text-black dark:text-gray-200">
                        Documento (CPF/CNPJ)
                    </label>
                    <input
                        type="text"
                        value={initialData.document}
                        disabled
                        className="w-full max-w-md p-2 border border-gray rounded-md bg-gray-100 dark:bg-gray text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    />
                    <button
                        onClick={handleDocumentoChangeRequest}
                        className="text-sm text-[#1F467E] hover:underline mt-1 focus:outline-none"
                    >
                        Solicitar alteração Documento
                    </button>
                </div>
            </div>

            <div className="pt-2">
                <h3 className="text-lg font-medium text-black dark:text-gray-100 mb-3">Endereço</h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-black dark:text-gray-200">
                            CEP
                        </label>
                        <InputMask
                            mask="99999-999"
                            value={zipCode}
                            onChange={handleZipCodeChange}
                            disabled={!usuarioAdm}
                            className={`w-full p-2 border border-gray-300 rounded-md ${usuarioAdm ? 'bg-white dark:bg-gray-800 text-black dark:text-gray-100' : 'bg-gray-100 dark:bg-gray text-gray-500 dark:text-gray-400 cursor-not-allowed'}`}
                            placeholder="00000-000"
                        />
                        {errors.zipCode && <p className="text-red-500 text-sm">{errors.zipCode}</p>}
                    </div>

                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-black dark:text-gray-200">
                            Estado
                        </label>
                        <input
                            type="text"
                            value={state}
                            onChange={(e) => usuarioAdm && setState(e.target.value)}
                            disabled={!usuarioAdm}
                            className={`w-full p-2 border border-gray-300 rounded-md ${usuarioAdm ? 'bg-white dark:bg-gray-800 text-black dark:text-gray-100' : 'bg-gray-100 dark:bg-gray text-gray-500 dark:text-gray-400 cursor-not-allowed'}`}
                        />
                        {errors.state && <p className="text-red-500 text-sm">{errors.state}</p>}
                    </div>

                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-black dark:text-gray-200">
                            Cidade
                        </label>
                        <input
                            type="text"
                            value={city}
                            onChange={(e) => usuarioAdm && setCity(e.target.value)}
                            disabled={!usuarioAdm}
                            className={`w-full p-2 border border-gray-300 rounded-md ${usuarioAdm ? 'bg-white dark:bg-gray-800 text-black dark:text-gray-100' : 'bg-gray-100 dark:bg-gray text-gray-500 dark:text-gray-400 cursor-not-allowed'}`}
                        />
                        {errors.city && <p className="text-red-500 text-sm">{errors.city}</p>}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mt-4">
                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-black dark:text-gray-200">
                            Rua
                        </label>
                        <input
                            type="text"
                            value={street}
                            onChange={(e) => usuarioAdm && setStreet(e.target.value)}
                            disabled={!usuarioAdm}
                            className={`w-full p-2 border border-gray-300 rounded-md ${usuarioAdm ? 'bg-white dark:bg-gray-800 text-black dark:text-gray-100' : 'bg-gray-100 dark:bg-gray text-gray-500 dark:text-gray-400 cursor-not-allowed'}`}
                        />
                        {errors.street && <p className="text-red-500 text-sm">{errors.street}</p>}
                    </div>

                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-black dark:text-gray-200">
                            Número
                        </label>
                        <input
                            type="text"
                            value={number}
                            onChange={(e) => usuarioAdm && setNumber(e.target.value)}
                            disabled={!usuarioAdm}
                            className={`w-full p-2 border border-gray-300 rounded-md ${usuarioAdm ? 'bg-white dark:bg-gray-800 text-black dark:text-gray-100' : 'bg-gray-100 dark:bg-gray text-gray-500 dark:text-gray-400 cursor-not-allowed'}`}
                        />
                        {errors.number && <p className="text-red-500 text-sm">{errors.number}</p>}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mt-4">
                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-black dark:text-gray-200">
                            Bairro
                        </label>
                        <input
                            type="text"
                            value={bairro}
                            onChange={(e) => usuarioAdm && setBairro(e.target.value)}
                            disabled={!usuarioAdm}
                            className={`w-full p-2 border border-gray-300 rounded-md ${usuarioAdm ? 'bg-white dark:bg-gray-800 text-black dark:text-gray-100' : 'bg-gray-100 dark:bg-gray text-gray-500 dark:text-gray-400 cursor-not-allowed'}`}
                        />
                        {errors.bairro && <p className="text-red-500 text-sm">{errors.bairro}</p>}
                    </div>

                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-black dark:text-gray-200">
                            Complemento
                        </label>
                        <input
                            type="text"
                            value={complement}
                            onChange={(e) => usuarioAdm && setComplement(e.target.value)}
                            disabled={!usuarioAdm}
                            className={`w-full p-2 border border-gray-300 rounded-md ${usuarioAdm ? 'bg-white dark:bg-gray-800 text-black dark:text-gray-100' : 'bg-gray-100 dark:bg-gray text-gray-500 dark:text-gray-400 cursor-not-allowed'}`}
                        />
                    </div>
                </div>
            </div>

            {usuarioAdm && (
                <div className="pt-4">
                    <button
                        onClick={handleUpdateInfo}
                        className="px-4 py-2 bg-[#1F467E] text-white rounded-md hover:bg-[#2a5a9e] transition-colors focus:outline-none focus:ring-2 focus:ring-[#1F467E] focus:ring-offset-2"
                    >
                        Atualizar Informações
                    </button>
                </div>
            )}

            {/* Modal para visualização da logo */}
            {showImageModal && (
                <ImagePreviewOverlay onClick={() => setShowImageModal(false)}>
                    <ImagePreviewContent onClick={(e: any) => e.stopPropagation()}>
                        <PreviewImage src={logoPreview} alt="Visualização da logo" />
                        <CloseButton onClick={() => setShowImageModal(false)}>
                            <FaTimes />
                        </CloseButton>
                        <DownloadButton
                            onClick={(e: any) => {
                                e.stopPropagation();
                                const filename = 'logo-empresa';
                                handleDownloadFile(logoPreview, filename);
                            }}
                            title="Baixar logo"
                        >
                            <FaDownload />
                        </DownloadButton>
                    </ImagePreviewContent>
                </ImagePreviewOverlay>
            )}
        </div>
    );
};

export default InformacoesTab;